import {
  Box,
  Button,
  Center,
  Collapse,
  Heading,
  Stack,
  type StyleProps,
  Text,
  type TextProps,
} from '@chakra-ui/react'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import React from 'react'

import ShwLink from '@/components/ShwLink'
import Icon from '@/components/UI/Icon/Icon'

import Card from './Worlds/UI/Card'

type QuickLinkBase = {
  title: string
  subtitle?: string | null
  icon?: IconDefinition
  iconColor?: StyleProps['color']
  bgColor?: StyleProps['bgColor']
}

export type QuickLink =
  | (QuickLinkBase & { href: string; onClick?: never })
  | (QuickLinkBase & { onClick: () => void; href?: never })

const MAX_TO_SHOW = 8

function LinkItem({ link, titleProps }: { link: QuickLink; titleProps?: TextProps }): JSX.Element {
  const { title, href, onClick, icon, iconColor, bgColor, subtitle } = link
  const content = (
    <Box
      _hover={{ bgColor: 'bg.muted' }}
      _active={{ bgColor: 'bg.muted' }}
      textDecoration="none"
      borderRadius="8px"
      p="8px"
      cursor="pointer"
    >
      <Stack direction="row" align="center" spacing="8px">
        {icon != null ? (
          <Center width="26px" height="26px" bg={bgColor} borderRadius="4px">
            <Icon size="sm" faIcon={icon} color={iconColor} omitContainer />
          </Center>
        ) : null}
        <Stack direction="column" spacing="0px">
          <Text fontWeight={600} {...titleProps}>
            {title}
          </Text>
          {subtitle == null ? null : (
            <Text fontWeight={600} color="primary.fg" variant="supporting">
              {subtitle}
            </Text>
          )}
        </Stack>
      </Stack>
    </Box>
  )

  if (href != null) {
    return <ShwLink href={href}>{content}</ShwLink>
  }
  return <Box onClick={onClick}>{content}</Box>
}

function LinkList({
  links,
  titleProps,
}: {
  links: QuickLink[]
  titleProps?: TextProps
}): JSX.Element {
  return (
    <Stack direction="column" spacing="0px">
      {links.map(link => (
        <LinkItem key={link.title} link={link} titleProps={titleProps} />
      ))}
    </Stack>
  )
}

export default function QuickLinks({
  links,
  hideHeading = false,
  titleProps,
}: {
  links: QuickLink[]
  hideHeading?: boolean
  titleProps?: TextProps
}): JSX.Element {
  const [showAll, setShowAll] = React.useState(false)

  return (
    <Card p="16px">
      {!hideHeading ? (
        <Heading size="lg" mb="8px" pl="8px" mt="8px">
          Quick Links
        </Heading>
      ) : null}
      <Box>
        <LinkList links={links.slice(0, MAX_TO_SHOW)} titleProps={titleProps} />
        {links.length > MAX_TO_SHOW ? (
          <>
            <Collapse in={showAll} style={{ margin: 0 }}>
              <LinkList links={links.slice(MAX_TO_SHOW)} titleProps={titleProps} />
            </Collapse>
            <Box pl="8px">
              <Button variant="ghost" onClick={() => setShowAll(x => !x)}>
                {showAll ? 'Show Fewer' : 'Show More'}
              </Button>
            </Box>
          </>
        ) : null}
      </Box>
    </Card>
  )
}
